import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Loader2, Users, MessageSquare, DollarSign, Plus } from "lucide-react";
import { ScrollArea } from "./ui/scroll-area";

interface UserStats {
  id: string;
  email: string | null;
  phone: string | null;
  budget: number;
  created_at: string;
  phone_verified: boolean;
  chat_count: number;
  total_spent: number;
  pending_requests: number;
  approved_requests: number;
  rejected_requests: number;
}

export const AdminUsersManagement = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [users, setUsers] = useState<UserStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserStats | null>(null);
  const [budgetAmount, setBudgetAmount] = useState("");
  const [addingBudget, setAddingBudget] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    checkAdminStatus();
  }, [user]);

  useEffect(() => {
    if (isAdmin) {
      fetchUsersStats();
    }
  }, [isAdmin]);

  const checkAdminStatus = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .eq("role", "admin")
      .single();

    setIsAdmin(!!data && !error);
    setLoading(false);
  };

  const fetchUsersStats = async () => {
    try {
      // Fetch all profiles
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("*")
        .order("created_at", { ascending: false });

      if (profilesError) throw profilesError;

      // Fetch chat analyses count per user
      const { data: chatCounts, error: chatError } = await supabase
        .from("chat_analyses")
        .select("user_id");

      if (chatError) throw chatError;

      // Fetch budget requests per user
      const { data: requests, error: requestsError } = await supabase
        .from("budget_purchase_requests")
        .select("user_id, amount, status");

      if (requestsError) throw requestsError;

      // Aggregate data
      const usersWithStats: UserStats[] = profiles.map((profile) => {
        const userChats = chatCounts.filter((c) => c.user_id === profile.id);
        const userRequests = requests.filter((r) => r.user_id === profile.id);
        
        const approvedRequests = userRequests.filter((r) => r.status === "approved");
        const totalSpent = approvedRequests.reduce((sum, r) => sum + Number(r.amount), 0);

        return {
          id: profile.id,
          email: profile.email,
          phone: profile.phone,
          budget: Number(profile.budget),
          created_at: profile.created_at,
          phone_verified: profile.phone_verified,
          chat_count: userChats.length,
          total_spent: totalSpent,
          pending_requests: userRequests.filter((r) => r.status === "pending").length,
          approved_requests: approvedRequests.length,
          rejected_requests: userRequests.filter((r) => r.status === "rejected").length,
        };
      });

      setUsers(usersWithStats);
    } catch (error) {
      console.error("Error fetching users stats:", error);
      toast.error(t("toast.requestFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleAddBudget = async () => {
    if (!selectedUser) return;

    const amount = parseFloat(budgetAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error(t("toast.validAmount"));
      return;
    }

    setAddingBudget(true);
    try {
      const newBudget = selectedUser.budget + amount;
      
      const { error } = await supabase
        .from("profiles")
        .update({ budget: newBudget })
        .eq("id", selectedUser.id);

      if (error) throw error;

      toast.success(t("toast.budgetAdded"));
      setBudgetAmount("");
      setDialogOpen(false);
      setSelectedUser(null);
      fetchUsersStats();
    } catch (error) {
      console.error("Error adding budget:", error);
      toast.error(t("toast.budgetUpdateFailed"));
    } finally {
      setAddingBudget(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("admin.accessDenied")}</CardTitle>
          <CardDescription>{t("admin.noPrivileges")}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("admin.totalUsers")}</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{users.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("admin.totalChats")}</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {users.reduce((sum, u) => sum + u.chat_count, 0)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("admin.totalRevenue")}</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${users.reduce((sum, u) => sum + u.total_spent, 0).toFixed(2)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t("admin.usersManagement")}</CardTitle>
          <CardDescription>{t("admin.viewAllUsers")}</CardDescription>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">{t("admin.noUsers")}</p>
          ) : (
            <ScrollArea className="h-[600px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("admin.joinDate")}</TableHead>
                    <TableHead>{t("admin.contact")}</TableHead>
                    <TableHead>{t("admin.verified")}</TableHead>
                    <TableHead>{t("admin.currentBudget")}</TableHead>
                    <TableHead>{t("admin.chatsCount")}</TableHead>
                    <TableHead>{t("admin.totalSpent")}</TableHead>
                    <TableHead>{t("admin.requests")}</TableHead>
                    <TableHead className="text-left">{t("admin.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        {new Date(user.created_at).toLocaleDateString('he-IL')}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {user.email && (
                            <span className="text-sm">{user.email}</span>
                          )}
                          {user.phone && (
                            <span className="text-xs text-muted-foreground">{user.phone}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.phone_verified ? "default" : "secondary"}>
                          {user.phone_verified ? t("admin.verified") : t("admin.notVerified")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">${user.budget.toFixed(2)}</span>
                          <Dialog open={dialogOpen && selectedUser?.id === user.id} onOpenChange={(open) => {
                            setDialogOpen(open);
                            if (!open) {
                              setSelectedUser(null);
                              setBudgetAmount("");
                            }
                          }}>
                            <DialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setDialogOpen(true);
                                }}
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>{t("admin.addBudget")}</DialogTitle>
                                <DialogDescription>
                                  {t("admin.addBudgetDesc")}
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                  <Label htmlFor="budget-amount">{t("budget.addAmount")} ($)</Label>
                                  <Input
                                    id="budget-amount"
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    placeholder="10.00"
                                    value={budgetAmount}
                                    onChange={(e) => setBudgetAmount(e.target.value)}
                                  />
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {t("admin.currentBudget")}: ${user.budget.toFixed(2)}
                                  {budgetAmount && !isNaN(parseFloat(budgetAmount)) && (
                                    <span className="block mt-1">
                                      {t("admin.newBudget")}: ${(user.budget + parseFloat(budgetAmount)).toFixed(2)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <DialogFooter>
                                <Button
                                  onClick={handleAddBudget}
                                  disabled={addingBudget || !budgetAmount}
                                >
                                  {addingBudget && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                  {t("budget.addBudget")}
                                </Button>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <MessageSquare className="h-4 w-4 text-muted-foreground" />
                          <span>{user.chat_count}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium text-green-600 dark:text-green-400">
                          ${user.total_spent.toFixed(2)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1 text-xs">
                          <span className="text-yellow-600 dark:text-yellow-400">
                            {t("admin.pending")}: {user.pending_requests}
                          </span>
                          <span className="text-green-600 dark:text-green-400">
                            {t("admin.approved")}: {user.approved_requests}
                          </span>
                          <span className="text-red-600 dark:text-red-400">
                            {t("admin.rejected")}: {user.rejected_requests}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
